// ============================================
// ESCANER.JS — Escáner de códigos de barras y QR
// Usa @zxing/library para escaneo con cámara
// ============================================

import React, { useState, useEffect, useRef } from 'react';

const PP = "'Poppins', sans-serif";
const colores = { principal: '#5c9adb', oscuro: '#2f5f9e', gris: '#4e5357', grisClaro: '#5e666e', verde: '#1D9E75', rojo: '#e05c5c', fondo: '#f3f4f4' };

function Escaner({ abierto, onCerrar, onEscaneo, placeholder }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const [codigoManual, setCodigoManual] = useState('');
  const [error, setError] = useState('');
  const [escaneando, setEscaneando] = useState(false);
  const [ultimoCodigo, setUltimoCodigo] = useState('');

  useEffect(() => {
    if (!abierto) return;
    let mounted = true;

    const iniciarEscaneo = async () => {
      try {
        const { BrowserMultiFormatReader } = await import('@zxing/library');
        const reader = new BrowserMultiFormatReader();
        readerRef.current = reader;

        const devices = await reader.listVideoInputDevices();
        if (devices.length === 0) {
          setError('No se detectó cámara');
          return;
        }
        // Preferir cámara trasera
        const trasera = devices.find(d => d.label.toLowerCase().includes('back') || d.label.toLowerCase().includes('trasera') || d.label.toLowerCase().includes('rear'));
        const deviceId = trasera ? trasera.deviceId : devices[devices.length - 1].deviceId;

        setEscaneando(true);
        reader.decodeFromVideoDevice(deviceId, videoRef.current, (result, err) => {
          if (!mounted) return;
          if (result) {
            const codigo = result.getText();
            setUltimoCodigo(codigo);
            setEscaneando(false);
            // Vibración suave
            if (navigator.vibrate) navigator.vibrate(100);
            if (onEscaneo) onEscaneo(codigo);
            reader.reset();
          }
        });
      } catch (e) {
        if (mounted) setError('No se pudo iniciar la cámara. Usa el campo manual.');
      }
    };

    iniciarEscaneo();

    return () => {
      mounted = false;
      if (readerRef.current) {
        try { readerRef.current.reset(); } catch (e) { /* ignore */ }
      }
    };
  }, [abierto, onEscaneo]);

  if (!abierto) return null;

  const handleManual = () => {
    if (!codigoManual.trim()) return;
    setUltimoCodigo(codigoManual.trim());
    if (onEscaneo) onEscaneo(codigoManual.trim());
    setCodigoManual('');
  };

  return (
    <>
      <div onClick={onCerrar} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9998 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: '#fff', borderRadius: '20px', padding: '24px', width: '90%', maxWidth: '420px', zIndex: 9999, boxShadow: '0 8px 30px rgba(0,0,0,0.3)', fontFamily: PP }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: colores.gris }}>{'📷'} Escanear código</div>
          <div onClick={onCerrar} style={{ width: '28px', height: '28px', borderRadius: '50%', background: colores.fondo, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '13px', color: colores.grisClaro }}>{'✕'}</div>
        </div>

        {/* Video de cámara */}
        <div style={{ position: 'relative', background: '#000', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px', aspectRatio: '4/3' }}>
          <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
          {escaneando && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '200px', height: '200px', border: '2px solid rgba(92,154,219,0.8)', borderRadius: '12px' }}>
              <div style={{ position: 'absolute', top: '-1px', left: '-1px', width: '30px', height: '30px', borderTop: '3px solid #5c9adb', borderLeft: '3px solid #5c9adb', borderRadius: '4px 0 0 0' }} />
              <div style={{ position: 'absolute', top: '-1px', right: '-1px', width: '30px', height: '30px', borderTop: '3px solid #5c9adb', borderRight: '3px solid #5c9adb', borderRadius: '0 4px 0 0' }} />
              <div style={{ position: 'absolute', bottom: '-1px', left: '-1px', width: '30px', height: '30px', borderBottom: '3px solid #5c9adb', borderLeft: '3px solid #5c9adb', borderRadius: '0 0 0 4px' }} />
              <div style={{ position: 'absolute', bottom: '-1px', right: '-1px', width: '30px', height: '30px', borderBottom: '3px solid #5c9adb', borderRight: '3px solid #5c9adb', borderRadius: '0 0 4px 0' }} />
            </div>
          )}
          {!escaneando && !error && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#fff', fontSize: '13px', textAlign: 'center' }}>Iniciando cámara...</div>
          )}
        </div>

        {error && (
          <div style={{ background: '#fff0f0', borderRadius: '8px', padding: '10px', fontSize: '12px', color: colores.rojo, marginBottom: '12px', textAlign: 'center' }}>{error}</div>
        )}

        {ultimoCodigo && (
          <div style={{ background: '#e8faf5', borderRadius: '8px', padding: '10px', fontSize: '12px', color: colores.verde, marginBottom: '12px', textAlign: 'center', fontWeight: '600' }}>
            {'✅'} Código detectado: {ultimoCodigo}
          </div>
        )}

        {/* Entrada manual */}
        <div style={{ fontSize: '11px', color: colores.grisClaro, marginBottom: '6px' }}>{placeholder || 'O escribe el código manualmente:'}</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            value={codigoManual}
            onChange={e => setCodigoManual(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleManual(); }}
            placeholder="Código de barras o serie..."
            style={{ flex: 1, padding: '10px 12px', border: '1px solid #d0d0d0', borderRadius: '8px', fontSize: '13px', outline: 'none', color: colores.gris, fontFamily: PP, boxSizing: 'border-box' }}
          />
          <button onClick={handleManual} style={{ padding: '10px 16px', background: colores.oscuro, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: PP }}>Buscar</button>
        </div>
      </div>
    </>
  );
}

export default Escaner;
